import React, { useState, useContext, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	Accordion,
	AccordionContext,
	Col,
	Row,
	useAccordionButton,
	Form,
	Card,
	Button,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useLeadsStore from '../../../store/LeadsStore';
import Icon from '../../Icon/Icon';
import Btn from '../../Btn/Btn';
import GroupedVehicleFilterFields from './GroupedVehicleFilterFields';
import { useAuth } from '../../../hooks/useAuth';
import { VehicleStateEnum } from '../../../api';
import { canFilterByUser, sanitizeStoredFilters } from 'src/vehicleList/filterOptions';

const VehicleFilters =  forwardRef(({ headerGroups, setAllFilters, loading, presetFilter, filterIntialState, onFiltersChange, filterOptions, showSearch, manualApply }: any, ref) => {

	const expandMenu = useAccordionButton('0');
	const { activeEventKey } = useContext(AccordionContext);
	const { userRole } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const currentURL = location.pathname;
	let initialState = {}
	const { t } = useTranslation()
	const { setCheckedLeads } = useLeadsStore();
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	const hasApplied = useRef(false);
	const hasAppliedPreset = useRef(false);
	const [advancedOpen, setAdvancedOpen] = useState(false);

	useImperativeHandle(ref, () => ({ expand: (bool: boolean) => {
		if (showSearch) {
			setAdvancedOpen(bool);
			return;
		}
		// @ts-ignore
		expandMenu(undefined);
		setIsFiltersOpen(bool);
	}, isFiltersOpen: showSearch ? advancedOpen : isFiltersOpen, filterState }), [expandMenu, isFiltersOpen, advancedOpen, showSearch]);

	// if user is Fleet_manager or SALES and we are in the wishlist page then it needs to return this data
	// ALSO if user is Fleet_manager and we are in the available-vehicles page we need to set initial state like this
	// because same component, same url, but only Fleet_manager see different colons compare to all other users
	// if ((
	// 	userRole === Roles.Fleet_manager || userRole === Roles.Sales || userRole === Roles.Master || userRole === Roles.Backoffice) && currentURL.includes('/wishlist')
	// 	|| userRole === Roles.Fleet_manager && currentURL.includes('/available-vehicles')) {

	// if user is Fleet_manager or SALES and we are in the wishlist page then it needs to return this data
	// ALSO if user is Fleet_manager and we are in the available-vehicles page we need to set initial state like this
	// because same component, same url, but only Fleet_manager see different colons compare to all other users
	// if ((userRole === RoleEnum.Fleet_manager || userRole === Roles.Sales || userRole === Roles.Master || userRole === Roles.Backoffice) && currentURL.includes('/wishlist')

	if (currentURL.includes('/opportunities')) {
		initialState = {
			status_description: [],
		};
	}

	const getCurrentFilter = () => {
		switch (true) {
			case currentURL.includes('/promo-vehicles'):
				return "vehiclePromo-filters";
			case currentURL.includes('/wishlist'):
				return "vehicleWishlist-filters";
			case currentURL.includes('/available-vehicles'):
				return "vehicleAvailable-filters";
			case currentURL.includes('/opportunities'):
				return "pudList-filters";
			default:
				return '';
		}
	};

	const toServerFilters = (state) =>
		sanitizeStoredFilters(
			state,
			{ statoCorrente: filterOptions?.stato_corrente?.map((option) => option.value) },
			userRole
		);

	const readSavedFilters = () => {
		const saved = sessionStorage.getItem(getCurrentFilter());
		if (!saved) return filterIntialState;

		try {
			const parsed = JSON.parse(saved);
			return showSearch ? toServerFilters(parsed) : parsed;
		} catch {
			return filterIntialState;
		}
	};

	const saveFilters = (state) => {
		if (currentURL.includes('/promo-vehicles')) {
			sessionStorage.setItem("vehiclePromo-filters", JSON.stringify(state));
		} else if (currentURL.includes('/wishlist')) {
			sessionStorage.setItem("vehicleWishlist-filters", JSON.stringify(state));
		} else if (currentURL.includes('/opportunities')) {
			sessionStorage.setItem("pudList-filters", JSON.stringify(state));
		} else if (currentURL.includes('/available-vehicles')) {
			sessionStorage.setItem("vehicleAvailable-filters", JSON.stringify(state));
		} else if (currentURL.includes('/leads')) {
			sessionStorage.setItem("leads", JSON.stringify(state));
		}
	};

	const [filterState, setFilterState] = useState(readSavedFilters);
	// Cio' che la lista sta mostrando: con manualApply puo' differire da filterState.
	const [appliedState, setAppliedState] = useState(filterState);
	const applyFilters = (filterState) => {
		// turn the filteState object into an array
		const filtersObjectArray: { id: string; value: any }[] = [];
		for (const [key, value] of Object.entries(filterState)) {
			filtersObjectArray.push({ id: key, value });
		}
		setAllFilters(filtersObjectArray);
	};

	const applyNow = (next) => {
		setAppliedState(next);
		saveFilters(next);
		applyFilters(next);
		onFiltersChange?.(next);
	};

	const handleSearch = () => applyNow(filterState);

	const applyFilter = (id: string, value: unknown) => {
		setFilterState((prev) => ({ ...prev, [id]: value }));
		applyNow({ ...appliedState, [id]: value });
	};

	const removeFilter = (id: string) =>
		applyFilter(id, filterIntialState?.[id] ?? '');

	const handleKeyDown = (event) => {
		if (event.code !== 'Enter' && event.keyCode !== 13) return;

		const target = event.target as HTMLElement;
		if (target.tagName !== 'INPUT' || target.getAttribute('role') === 'combobox') {
			return;
		}

		handleSearch();
	};

	const handleReset = () => {
		setFilterState(filterIntialState);
		if (manualApply) applyNow(filterIntialState);
	};

	useEffect(() => {
		// Lato server `loading` torna false a ogni pagina: il preset va applicato
		// una volta sola, o riporterebbe la lista alla prima pagina ogni volta.
		if (manualApply && hasAppliedPreset.current) return;

		if (!loading && presetFilter) {
			hasAppliedPreset.current = true;
			const resolved = Object.values(VehicleStateEnum).includes(presetFilter)
				? { ...filterState, stato_corrente: [presetFilter] }
				: presetFilter;
			const next = showSearch ? toServerFilters(resolved) : resolved;
			setFilterState(next);
			if (manualApply) {
				applyNow(next);
				navigate(location.pathname, { replace: true });
			}
		}
	}, [loading]);


	useEffect(() => {
		// reset the checked lead List from the leads section
		setCheckedLeads([])

		if (!manualApply || !hasApplied.current) {
			hasApplied.current = true;
			applyNow(filterState);
		}
	}, [filterState]);

	// check if there there are SecondaryFilters and if  there are display the Aggiungi Filtri div
	const hasSecondaryFilter = headerGroups.some((headerGroup) =>
		headerGroup.headers.some((column) => column.SecondaryFilter)
	);

	const actions = (
		<>
			<Button className="filter-reset-btn" onClick={handleReset}>
				{t('reset_filters', { defaultValue: 'Reset filtri' })}
			</Button>
			{manualApply && (
				<Btn
					className="d-inline-block ms-3"
					onClick={handleSearch}
					icon="arrow-right"
				>
					{showSearch
						? t('apply_filters', { defaultValue: 'Applica filtri' })
						: t('search', { defaultValue: 'Cerca' })}
				</Btn>
			)}
		</>
	);

	return (
		<Card
			className="filters-card"
			onKeyDown={manualApply ? handleKeyDown : undefined}
		>
			<Card.Header className="border-0">
				{showSearch ? (
					<GroupedVehicleFilterFields
						headerGroups={headerGroups}
						filterState={filterState}
						setFilterState={setFilterState}
						filterOptions={filterOptions}
						userRole={userRole}
						appliedState={appliedState}
						advancedOpen={advancedOpen}
						onToggleAdvanced={() => setAdvancedOpen((prev) => !prev)}
						onRemoveFilter={removeFilter}
						onApplyFilter={applyFilter}
						actions={actions}
					/>
				) : (
					<Row>
						{headerGroups.map((headerGroup) =>
							headerGroup.headers.map((column) => {
								if (column.id === 'utente' && showSearch && !canFilterByUser(userRole)) {
									return null;
								}

								return (
									column.PrimaryFilter ? (
										<Col key={column.id} lg={6}>
											<Form.Group className="mb-3">
												<div key={column.id}>
													<Form.Label htmlFor={column.id}>
														{column.render('Header')}
													</Form.Label>
													{column.render('PrimaryFilter', {
														props: {
															filterState,
															setFilterState,
															isCreatable: column.isCreatable,
															options: filterOptions?.[column.id],
														},
													})}
												</div>
											</Form.Group>
										</Col>
									) : null)
							}
							)
						)}
					</Row>
				)}
			</Card.Header>
			{!showSearch && (
				<Card.Body className="py-0">
					<Accordion.Collapse eventKey="0">
						<>
							{hasSecondaryFilter && (
								<div className="my-4 pb-1 h6 border-bottom">
									{t('added_filters')}
								</div>
							)}
							<Row>
								{headerGroups.map((headerGroup) =>
									headerGroup.headers.map((column) =>
										column.SecondaryFilter &&
										!(column.id === 'utente' && showSearch && !canFilterByUser(userRole)) ? (
											<Col key={column.id} lg={6}>
												<Form.Group className="mb-3">
													<div key={column.id}>
														<Form.Label htmlFor={column.id}>
															{column.render('Header')}
														</Form.Label>
														{column.render('SecondaryFilter', {
															props: {
																filterState,
																setFilterState,
																type: column.inputType,
																isCreatable: column.isCreatable,
																options: filterOptions?.[column.id],
															},
														})}
													</div>
												</Form.Group>
											</Col>
										) : null
									)
								)}
							</Row>
						</>
					</Accordion.Collapse>
				</Card.Body>
			)}
			{!showSearch && <Card.Footer className="pt-0 border-0">
				<Row>
					<Col lg={4}></Col>
					<Col lg={4} className="open-filtri-- text-center">

						{/* <div
							className="open-filtri-container"
							onClick={(event: React.MouseEvent) => {
								event.preventDefault();
								expandMenu(event);
							}}
						>
								<div className="open-filtri-label">
									{t('added_filters')}
								</div>

							<div>
								<div>
									<Icon
										className={
											(activeEventKey === '0' ? 'is-open' : '') +
											' mt-3 expand-icon'
										}
										name="arrow-right"
									></Icon>
								</div>
							</div>
						</div>
 */}


						{hasSecondaryFilter && !showSearch && (
							<div
								className="open-filtri-container"
								onClick={(event: React.MouseEvent) => {
									event.preventDefault();
									expandMenu(event);
									setIsFiltersOpen(prev => !prev)
								}}
							>
								<div className="open-filtri-label">{t('added_filters')}</div>

								<div>
									<div>
										<Icon
											className={(activeEventKey === '0' ? 'is-open' : '') + ' mt-3 expand-icon'}
											name="arrow-right"
										></Icon>
									</div>
								</div>
							</div>
						)}
					</Col>
					<Col lg={4} className="text-end">
						{/* <Link to="#" onClick={handleReset} className="btn me-3">
							Reset
						</Link> */}
						{/* <Btn onClick={handleSearch} icon="arrow-right">
							Cerca
						</Btn> */}
						{actions}
					</Col>
				</Row>
			</Card.Footer>}
		</Card>
	);
});

export default VehicleFilters;
